import debounce from "lodash.debounce";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import useAxios from "./useAxios";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 100;

/**
 * Shared page/page_size/status_category state for a batch-list grid (BatchList.js),
 * synced to the URL query string so a filtered/paginated view is directly linkable.
 * Consolidates what used to be duplicated per-page state + fetch effects across every
 * views/base/*.js batch-list page.
 */
const useBatchListState = (apiConfig, { client, extraParams } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [metaData, setMetaData] = useState({
    page: DEFAULT_PAGE,
    page_size: DEFAULT_PAGE_SIZE,
    total: 1,
  });
  const [response, call, loading] = useAxios({ ...apiConfig });

  const currentPage = Number(searchParams.get("page")) || DEFAULT_PAGE;
  const pageSize = Number(searchParams.get("page_size")) || DEFAULT_PAGE_SIZE;
  const statusCategories = searchParams.getAll("status_category");
  const statusCategoriesKey = statusCategories.join(",");
  const extraParamsKey = JSON.stringify(extraParams || {});

  const debouncedFetchData = debounce((query) => {
    call(query);
  }, 500);

  useEffect(() => {
    setMetaData({
      page: DEFAULT_PAGE,
      page_size: DEFAULT_PAGE_SIZE,
      total: 1,
    });
    setData([]);
  }, [client]);

  useEffect(() => {
    debouncedFetchData({
      params: {
        page: currentPage,
        page_size: pageSize,
        client,
        ...extraParams,
        ...(statusCategories.length ? { status_category: statusCategories } : {}),
      },
    });

    return () => {
      debouncedFetchData.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, client, statusCategoriesKey, extraParamsKey]);

  useEffect(() => {
    if (response && response.status) {
      const responseData = response.data || {};
      setMetaData({
        page: responseData.page ?? DEFAULT_PAGE,
        page_size: responseData.page_size ?? DEFAULT_PAGE_SIZE,
        total: responseData.total ?? 0,
      });
      setData(Array.isArray(responseData.data) ? responseData.data : []);
    }
  }, [response]);

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      next.delete(key);
      if (Array.isArray(value)) {
        value.forEach((v) => next.append(key, v));
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const handlePageChange = (page) => updateParams({ page });

  const handlePageSizeChange = (newPageSize) =>
    updateParams({ page: DEFAULT_PAGE, page_size: newPageSize });

  const handleStatusCategoryChange = (category) => {
    const next = statusCategories.includes(category)
      ? statusCategories.filter((c) => c !== category)
      : [...statusCategories, category];
    updateParams({ page: DEFAULT_PAGE, status_category: next });
  };

  return {
    data,
    loading,
    metaData,
    response,
    currentPage,
    pageSize,
    statusCategories,
    handlePageChange,
    handlePageSizeChange,
    handleStatusCategoryChange,
  };
};

export default useBatchListState;
